import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { environment } from '../../environments/environment';
import { AvailabilityBlocksComponent } from './availability-blocks/availability-blocks.component';

interface Resource {
  id: string;
  name: string;
  resourceTypeId: string;
  attributeValues?: ResourceAttributeValue[];
  selected?: boolean;
}

interface DaySelection {
  day: string;
  code: string;
  selected: boolean;
}

interface Availability {
  id?: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

interface ResourceAttribute {
  id: string;
  name: string;
  slug: string | null;
  type: string;
  order: number | null;
  selected?: boolean;
  value?: string;
}

interface ResourceAttributeValue {
  id?: string;
  resourceAttributeId: string;
  resourceId: string;
  value: string;
}

@Component({
  selector: 'app-copy-availability-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatIconModule,
    MatExpansionModule,
    MatChipsModule,
    MatDividerModule,
    AvailabilityBlocksComponent
  ],
  templateUrl: './copy-availability-dialog.component.html',
  styleUrls: ['./copy-availability-dialog.component.scss']
})
export class CopyAvailabilityDialogComponent implements OnInit {
  targetResources: Resource[] = [];
  sourceAvailability: Availability[] = [];
  availableAttributes: ResourceAttribute[] = [];
  loading = false;
  copying = false;
  allSelected = false;
  someSelected = false;
  allAttributesSelected = false;
  someAttributesSelected = false;
  copyAvailability = true;
  copyAttributes = false;
  overwriteExisting = false;
  
  days: DaySelection[] = [
    { day: 'Monday', code: 'MO', selected: true },
    { day: 'Tuesday', code: 'TU', selected: true },
    { day: 'Wednesday', code: 'WE', selected: true },
    { day: 'Thursday', code: 'TH', selected: true },
    { day: 'Friday', code: 'FR', selected: true },
    { day: 'Saturday', code: 'SA', selected: true },
    { day: 'Sunday', code: 'SU', selected: true }
  ];

  private apiBaseUrl = environment.apiBaseUrl;
  private organisationId: string = '';
  private dayMapping: { [key: string]: number } = {
    'MO': 1, 'TU': 2, 'WE': 3, 'TH': 4, 'FR': 5, 'SA': 6, 'SU': 7
  };

  constructor(
    public dialogRef: MatDialogRef<CopyAvailabilityDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      sourceResource: Resource,
      resourceTypeId: string,
      attributes?: ResourceAttribute[],
      attributeValues?: ResourceAttributeValue[]
    },
    private http: HttpClient,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    const storedId = localStorage.getItem('selectedOrganisationId');
    if (storedId) {
      this.organisationId = storedId;
      this.loadTargetResources();
      this.loadSourceAvailability();
      this.setupAttributes();
    }
  }

  setupAttributes() {
    if (this.data.attributes && this.data.attributes.length > 0) {
      this.availableAttributes = this.data.attributes.map(attr => {
        const attrValue = this.data.attributeValues?.find(
          av => av.resourceAttributeId === attr.id
        );
        return {
          ...attr,
          selected: false,
          value: attrValue?.value || ''
        };
      }).sort((a, b) => (a.order || 0) - (b.order || 0));
    }
  }

  loadTargetResources() {
    this.loading = true;
    
    this.http.get<Resource[]>(
      `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.data.resourceTypeId}/resources`
    ).subscribe({
      next: (resources) => {
        // Filter out the source resource, add selected property, and sort by name
        this.targetResources = resources
          .filter(r => r.id !== this.data.sourceResource.id)
          .map(r => ({ ...r, selected: false }))
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load resources:', err);
        this.snackBar.open('Failed to load resources', 'Close', {
          duration: 3000
        });
        this.loading = false;
      }
    });
  }

  loadSourceAvailability() {
    this.http.get<Availability[]>(
      `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.data.resourceTypeId}/resources/${this.data.sourceResource.id}/availabilities`
    ).subscribe({
      next: (availability) => {
        console.log('Loaded availability:', availability);
        this.sourceAvailability = availability || [];
      },
      error: (err) => {
        console.error('Failed to load availability:', err);
        // Continue without availability data
        this.sourceAvailability = [];
      }
    });
  }

  toggleAll(checked: boolean) {
    this.targetResources.forEach(r => r.selected = checked);
    this.updateSelectionState();
  }

  updateSelectionState() {
    const selectedCount = this.targetResources.filter(r => r.selected).length;
    this.allSelected = selectedCount === this.targetResources.length && this.targetResources.length > 0;
    this.someSelected = selectedCount > 0;
  }

  toggleAllAttributes(checked: boolean) {
    this.availableAttributes.forEach(a => a.selected = checked);
    this.updateAttributeSelectionState();
  }

  updateAttributeSelectionState() {
    const selectedCount = this.availableAttributes.filter(a => a.selected).length;
    this.allAttributesSelected = selectedCount === this.availableAttributes.length && this.availableAttributes.length > 0;
    this.someAttributesSelected = selectedCount > 0;
  }

  get canSubmit(): boolean {
    return this.getValidationError() === null && !this.loading;
  }

  getValidationError(): string | null {
    if (this.loading) {
      return 'Loading resources...';
    }
    if (!this.copyAvailability && !this.copyAttributes) {
      return 'Select at least availability or attributes to copy';
    }
    if (!this.targetResources.some(r => r.selected)) {
      return 'Select at least one target resource';
    }
    if (this.copyAvailability && !this.days.some(d => d.selected)) {
      return 'Select at least one day for availability';
    }
    if (this.copyAttributes && this.availableAttributes.length > 0 && !this.availableAttributes.some(a => a.selected)) {
      return 'Select at least one attribute to copy';
    }
    return null;
  }

  getSelectedCount(): number {
    return this.targetResources.filter(r => r.selected).length;
  }

  getSelectedAttributeCount(): number {
    return this.availableAttributes.filter(a => a.selected).length;
  }

  getAttributeValue(attr: ResourceAttribute): string {
    if (!attr.value) return 'No value';

    if (attr.type === 'select' && attr.slug) {
      const slugValues = attr.slug.split(',').map(s => s.trim());
      const displayValues = attr.name.split(',').map(s => s.trim());
      const index = slugValues.indexOf(attr.value);
      return index >= 0 ? displayValues[index] || attr.value : attr.value;
    }

    return attr.value;
  }

  getFilteredAvailability(): Availability[] {
    const selectedDays = this.days
      .filter(d => d.selected)
      .map(d => this.dayMapping[d.code]);
    
    return this.sourceAvailability.filter(slot => 
      selectedDays.includes(slot.dayOfWeek)
    );
  }

  getDayName(dayOfWeek: number): string {
    const dayNames = ['', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
    return dayNames[dayOfWeek] || '';
  }

  updateAvailabilityPreview() {
    // Trigger change detection
  }

  onCancel() {
    this.dialogRef.close();
  }

  onCopy() {
    const validationError = this.getValidationError();
    if (validationError) {
      this.snackBar.open(validationError, 'Close', { duration: 3000 });
      return;
    }
    if (this.copying) return;

    const selectedResourceIds = this.targetResources
      .filter(r => r.selected)
      .map(r => r.id);

    this.copying = true;

    // Copy both availability and attributes
    const promises: Promise<any>[] = [];

    if (this.copyAvailability) {
      const selectedDays = this.days
        .filter(d => d.selected)
        .map(d => d.code);

      promises.push(
        this.http.post<{ message: string; copiedCount: number; deletedCount: number }>(
          `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.data.resourceTypeId}/resources/${this.data.sourceResource.id}/copy-availability`,
          {
            targetResourceIds: selectedResourceIds,
            days: selectedDays,
            overwriteExisting: this.overwriteExisting
          }
        ).toPromise()
      );
    }

    if (this.copyAttributes && this.availableAttributes.some(a => a.selected)) {
      const selectedAttributes = this.availableAttributes
        .filter(a => a.selected)
        .map(a => ({
          attributeId: a.id,
          value: a.value || ''
        }));

      promises.push(
        this.http.post(
          `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.data.resourceTypeId}/resources/${this.data.sourceResource.id}/copy-attributes`,
          {
            targetResourceIds: selectedResourceIds,
            attributes: selectedAttributes
          }
        ).toPromise()
      );
    }

    Promise.all(promises)
      .then((results) => {
        this.copying = false;
        let message = 'Copied to ' + selectedResourceIds.length + ' resource(s): ';
        const parts = [];
        if (this.copyAvailability) {
          const availabilityResult = results.find(r => r?.deletedCount !== undefined);
          if (this.overwriteExisting && availabilityResult?.deletedCount > 0) {
            parts.push(`availability (${availabilityResult.deletedCount} existing rules replaced)`);
          } else {
            parts.push('availability');
          }
        }
        if (this.copyAttributes) parts.push('attributes');
        message += parts.join(' and ');

        this.snackBar.open(message, 'Close', {
          duration: 3000
        });
        this.dialogRef.close({ success: true, refreshRequired: true });
      })
      .catch((err) => {
        this.copying = false;
        console.error('Failed to copy:', err);
        this.snackBar.open('Failed to copy data', 'Close', {
          duration: 3000
        });
      });
  }
}